/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  recordTriggerDropped,
  recordArmedWithoutProfile,
  recordArmThrew,
  type SosInstrumentationSink,
} from "@/utils/safecircle/sosInstrumentation";
import { useSOSEventContext } from "@/contexts/SOSEventContext";
import { useSafeCircleProfiles } from "@/hooks/useSafeCircleProfiles";
import { useEmergencyContacts } from "@/hooks/useEmergencyContacts";
import { useSafeCircleStore } from "@/stores/safeCircleStore";
import { useVolumeButtonTrigger } from "@/hooks/useVolumeButtonTrigger";
import { logUserAction } from "@/utils/errorLogger";
import type { SOSTriggerMethod } from "@/types/safecircle";
import { sosTriggerBus } from "./sosTriggerBus";
import StealthMode from "./StealthMode";

const SHAKE_THRESHOLD_MPS2 = 15;
const SHAKE_WINDOW_MS = 3000;

// Sentry adapter for the pure SOS instrumentation helpers (mirrors the
// sentrySink in useSOSEvent). Module-level: stable identity, no per-render
// churn, and the bus subscriber closes over it safely.
const sentrySink: SosInstrumentationSink = {
  addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
  captureMessage: (message, ctx) => Sentry.captureMessage(message, ctx),
  captureException: (err, ctx) => Sentry.captureException(err, ctx),
};

/**
 * Mounted once inside the authenticated app shell. Subscribes to all SOS
 * triggers (in-app button via `sosTriggerBus.fire`, device shake via
 * DeviceMotionEvent, volume-button stub) and renders the active SOS UI —
 * either the full `SOSActiveScreen` or the decoy `StealthMode` wrapper for
 * opted-in Women profiles.
 */
export default function SOSTrigger() {
  const sos = useSOSEventContext();
  const { profiles, addProfile } = useSafeCircleProfiles();
  const { contacts: emergencyContacts } = useEmergencyContacts();
  const activeProfileId = useSafeCircleStore((s) => s.activeProfileId);
  const activeProfile =
    profiles.find((p) => p.profileId === activeProfileId) ?? profiles[0] ?? null;
  const armBusyRef = useRef(false);

  // Keep the latest values in refs so the bus subscriber (created once) always
  // reads current state without re-subscribing.
  const ensureRef = useRef({ activeProfile, addProfile, emergencyContacts });
  ensureRef.current = { activeProfile, addProfile, emergencyContacts };

  // Subscribe to the in-app trigger bus.
  useEffect(() => {
    const unsub = sosTriggerBus.subscribe(async (method) => {
      // A trigger arriving while a SOS is already active / arming is dropped.
      // Instrument it: to the tapper this looks identical to success, so a
      // dropped SOS would otherwise be invisible.
      if (sos.active || armBusyRef.current) {
        recordTriggerDropped(sentrySink, {
          method,
          active: sos.active,
          arming: armBusyRef.current,
        });
        return;
      }
      armBusyRef.current = true;
      try {
        const { activeProfile, addProfile, emergencyContacts } = ensureRef.current;
        let profile = activeProfile;
        // Part B — fire SOS even without a SafeCircle profile, AS LONG AS there
        // are emergency contacts to text. Auto-create a minimal default profile
        // so the proven profile-keyed SOS state machine runs unchanged; the SMS
        // fan-out (in useSOSEvent) then reaches the emergency contacts.
        if (!profile && emergencyContacts.length > 0) {
          const id = await addProfile("My Safety", "private", "self");
          if (id) {
            // arm()/confirm/fanOut read only profileId, name, mode (+ optional
            // stealthConfig) — see useSOSEvent. We construct exactly those from
            // the just-created profile; addProfile persisted the full record.
            profile = {
              profileId: id,
              name: "My Safety",
              mode: "private",
            } as NonNullable<typeof activeProfile>;
          }
        }
        // No profile AND no contacts → nothing to notify; don't arm (the AI
        // button routes such users to setup before ever reaching the bus).
        // The button path guards this earlier, but shake / volume / AI can
        // reach the bus unguarded — capture it so a silently-dropped SOS
        // (user thinks help is coming; nothing armed) is not invisible.
        if (!profile) {
          recordArmedWithoutProfile(sentrySink, method);
          return;
        }
        await sos.arm({ triggerMethod: method, profile });
        logUserAction("sos_triggered", { method, mode: profile.mode });
      } catch (err) {
        // Previously a try/finally with NO catch: a throw from addProfile() or
        // sos.arm() became an unhandled rejection — the single highest-value
        // silent failure in the SOS trigger chain (safety action fails, no
        // crash, no signal). Capture it. We do NOT change UX/retry here; the
        // SOS button's own onPress catch owns the user-facing 911 fallback.
        recordArmThrew(sentrySink, err, method);
      } finally {
        armBusyRef.current = false;
      }
    });
    return unsub;
  }, [sos]);

  // Shake detection — uses DeviceMotionEvent which works in browser and in
  // Capacitor webviews. iOS 13+ requires a user-gesture-gated permission
  // request, which the "enable shake trigger" button in settings should call.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("DeviceMotionEvent" in window)) return;
    const buffer: number[] = [];
    const handler = (e: DeviceMotionEvent) => {
      const a = e.acceleration;
      if (!a || a.x == null || a.y == null || a.z == null) return;
      const magnitude = Math.hypot(a.x, a.y, a.z);
      const now = Date.now();
      buffer.push(now);
      while (buffer.length > 0 && now - buffer[0] > SHAKE_WINDOW_MS) {
        buffer.shift();
      }
      if (magnitude > SHAKE_THRESHOLD_MPS2 && buffer.length >= 15) {
        buffer.length = 0;
        sosTriggerBus.fire("shake");
      }
    };
    window.addEventListener("devicemotion", handler);
    return () => window.removeEventListener("devicemotion", handler);
  }, []);

  useVolumeButtonTrigger({
    enabled: true,
    onTrigger: () => sosTriggerBus.fire("volume"),
  });

  if (!sos.active) return null;
  return <StealthMode />;
}

export { sosTriggerBus };
export type { SOSTriggerMethod };
